// Shared symbol-suggestion source: the same list the header search uses
// (static /yh_symbols.json, cached in localStorage) with the same ranking —
// symbol prefix matches first, then symbol contains, then company-name
// matches. Returns a suggest(query) function; safe to call every keystroke.
import { useCallback, useEffect, useState } from 'react';
import { POPULAR_SYMBOLS } from '../utils/constants';

const CACHE_KEY = 'yh_symbols_v1';

export default function useSymbolSuggestions() {
  const [allSymbols, setAllSymbols] = useState([]);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setAllSymbols(parsed);
          return undefined;
        }
      }
    } catch (_) { /* ignore cache issues */ }

    let mounted = true;
    fetch('/yh_symbols.json')
      .then((res) => res.json())
      .then((data) => {
        if (!mounted || !Array.isArray(data)) return;
        const normalized = data
          .map((item) => {
            if (typeof item === 'string') return { symbol: item.toUpperCase(), name: '' };
            if (item && typeof item === 'object' && item.symbol) {
              return { symbol: String(item.symbol).toUpperCase(), name: item.name ? String(item.name) : '' };
            }
            return null;
          })
          .filter(Boolean);
        setAllSymbols(normalized);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
        } catch (_) { /* ignore storage failures */ }
      })
      .catch(() => { /* fallback list keeps working */ });
    return () => { mounted = false; };
  }, []);

  return useCallback((query, limit = 8) => {
    const trimmed = (query || '').trim();
    if (!trimmed) return [];
    const upper = trimmed.toUpperCase();
    const source = allSymbols.length > 0 ? allSymbols : POPULAR_SYMBOLS;

    const prefix = source.filter((i) => i.symbol.startsWith(upper));
    const contains = source.filter((i) => !i.symbol.startsWith(upper) && i.symbol.includes(upper));
    const names = source.filter((i) => i.name && i.name.toLowerCase().includes(trimmed.toLowerCase()));

    const merged = [];
    const seen = new Set();
    for (const item of [...prefix, ...contains, ...names]) {
      if (!seen.has(item.symbol)) {
        seen.add(item.symbol);
        merged.push(item);
      }
      if (merged.length >= limit) break;
    }
    return merged;
  }, [allSymbols]);
}
